import { useEffect, useState } from "react";

import { Button, Table, Checkbox } from "rsuite";
import EditIcon from "@rsuite/icons/Edit";
import TrashIcon from "@rsuite/icons/Trash";

import ConfigurationPage from "templates/ConfigurationPage";
import AddBedManually from "./AddBedManually";
import ImportBed from "./ImportBed";
import TableWithSearch from "components/TableWithSearch";
import EditBed from "./EditBed";
import DeleteBed from "./DeleteBed";
import BedsTableEmptyMessage from "./BedsTableEmptyMessage";
import DeleteModal from "../../../../../components/DeleteModal";
import useRequestHandler from "hooks/useRequestHandler";

const { Column, HeaderCell, Cell } = Table;

const BedsList = ({ auth, locationId, history }) => {

    const isAdminUser = auth.currentUser?.role?.includes("admin");

    const [isBedsLoading, setIsBedsLoading] = useState(true);
    const [bedsData, setBedsData] = useState([]);
    const [addBedModalOpened, setAddBedModalOpened] = useState(false);
    const [importBedModalOpened, setImportBedModalOpened] = useState(false);
    const [editBedModalOpened, setEditBedModalOpened] = useState(false);
    const [deleteBedModalOpened, setDeleteBedModalOpened] = useState(false);
    const [editBedData, setEditBedData] = useState({
        sectionName: "",
        roomName: "",
        bedName: "",
        bedId: "",
    });
    const [isBatchDelete, setIsBatchDelete] = useState(false);
    const [checkedKeys, setCheckedKeys] = useState([]);
    const [deleteBatchBedsModalOpened, setDeleteBatchBedsModalOpened] = useState(false);

    let checked = false;
    let indeterminate = false;

    useEffect(() => getLocationBeds(), []);

    const getLocationBedsRequest = useRequestHandler({
        auth,
        apiArguments: [locationId],
        apiName: "getBedsByFacility",
        successMessageData: { show: false },
        errorMessageData: {
            title: "Error",
            message: "Something went wrong while loading beds data. Please try again.",
        }
    });

    const getLocationBeds = async () => {
        !isBedsLoading && setIsBedsLoading(true);
        await getLocationBedsRequest((data) => setBedsData(data));
        setIsBedsLoading(false);
    }

    const addImportBedsLayout = () => {
        return <>
            <Button appearance="primary" size='lg' color='cyan' className='import-bed-btn' onClick={() => setImportBedModalOpened(true)}>Import</Button>
            <Button appearance="ghost" size='lg' color='cyan' className='add-bed-btn' onClick={() => setAddBedModalOpened(true)}>Add</Button>
        </>
    }

    const goToEditDeleteBedModal = (data, { target }) => {
        if (target.ariaLabel === "trash" || (target.nearestViewportElement?.ariaLabel?.includes("trash"))) {
            setDeleteBedModalOpened(true);
            setEditBedData({
                sectionName: data.section,
                roomName: data.room_name,
                bedName: data.bed_name || "",
                bedId: data.bed_id
            });

        } else if (target.ariaLabel === "edit" || (target.nearestViewportElement?.ariaLabel?.includes("edit"))) {
            setEditBedModalOpened(true);
            setEditBedData({
                sectionName: data.section,
                roomName: data.room_name,
                bedName: data.bed_name || "",
                bedId: data.bed_id
            });
        }
    }

    if (bedsData.length && checkedKeys.length === bedsData.length) {
        checked = true;
    } else if (checkedKeys.length === 0) {
        checked = false;
    } else if (checkedKeys.length > 0 && checkedKeys.length < bedsData.length) {
        indeterminate = true;
    }

    const setKeysHandler = (keys) => {
        if (keys.length >= 1) {
            setIsBatchDelete(true);
        } else setIsBatchDelete(false);

        setCheckedKeys(keys);
    }

    const handleCheckAll = (_, checked) => {
        const keys = checked ? bedsData.map(item => item.bed_id) : [];
        setKeysHandler(keys);
    };

    const handleCheck = (value, checked) => {
        const keys = checked ? [...checkedKeys, value] : checkedKeys.filter(item => item !== value);
        setKeysHandler(keys);
    };

    const deleteData = () => (
        <div className={"list-delete-beds-data"}>
            <div>
                <strong>{checkedKeys.length}</strong> {`bed${checkedKeys.length > 1? "s" : ""} selected`}
            </div>
            <Button appearance="ghost" className={"delete-all-btn"} onClick={() => setDeleteBatchBedsModalOpened(true)}>Delete All</Button>
        </div>)

    const CheckCell = ({ rowData, onChange, checkedKeys, dataKey, ...props }) => (
        <Cell {...props} style={{ padding: 0 }}>
            <div style={{ lineHeight: "46px" }}>
                <Checkbox
                    value={rowData[dataKey]}
                    inline
                    onChange={onChange}
                    checked={checkedKeys.some(item => item === rowData[dataKey])}
                />
            </div>
        </Cell>
    );

    return (
        <>
            <ImportBed
                auth={auth}
                facilityId={locationId}
                modalOpen={importBedModalOpened}
                setModalOpen={setImportBedModalOpened}
                refreshData={getLocationBeds}
                data={bedsData}
            />

            <AddBedManually
                auth={auth}
                facilityId={locationId}
                modalOpen={addBedModalOpened}
                setModalOpen={setAddBedModalOpened}
                refreshData={getLocationBeds}
            />

            <EditBed
                auth={auth}
                facilityId={locationId}
                formValues={editBedData}
                modalOpen={editBedModalOpened}
                setModalOpen={setEditBedModalOpened}
                refreshData={getLocationBeds}
            />

            {locationId && isAdminUser &&
                <DeleteBed
                    auth={auth}
                    formValues={editBedData}
                    modalOpen={deleteBedModalOpened}
                    setModalOpen={setDeleteBedModalOpened}
                    refreshData={getLocationBeds}
                />
            }

            {isAdminUser && deleteBatchBedsModalOpened && <DeleteModal
                auth={auth}
                modalOpen={deleteBatchBedsModalOpened}
                setModalOpen={setDeleteBatchBedsModalOpened}
                contentLoader={"Deleting beds"}
                apiName={"deleteBeds"}
                apiArguments={checkedKeys}
                successMessageData={`Beds have been successfully deleted`}
                errorMessageData={"You cannot delete location please try later"}
                goBackUrl={`/configuration/locations/${locationId}`}
                history={history}
                setIsBatchDelete={setIsBatchDelete}
                setCheckedKeys={setCheckedKeys}
                refresh={getLocationBeds}
            >
           {`Are you sure you want to delete ${checkedKeys.length} bed${checkedKeys.length > 1 ? 's' : ''}?`}
            </DeleteModal>}

            <ConfigurationPage.Content>
                <TableWithSearch
                    data={bedsData}
                    dataLoading={isBedsLoading}
                    setData={setBedsData}
                    searchInputPlaceholder="Search beds"
                    rightContent={addImportBedsLayout}
                    searchBy="section"
                    onRowClick={goToEditDeleteBedModal}
                    isBatchDelete={isBatchDelete}
                    deletedData={deleteData}
                    locale={{ emptyMessage: <BedsTableEmptyMessage /> }}
                >
                    <Column width={50} align="center">
                        <HeaderCell style={{ padding: 0 }}>
                                <Checkbox
                                    inline
                                    checked={checked}
                                    indeterminate={indeterminate}
                                    onChange={handleCheckAll}
                                />
                        </HeaderCell>
                        <CheckCell dataKey="bed_id" checkedKeys={checkedKeys} onChange={handleCheck} />
                    </Column>

                    <Column flexGrow={4} sortable>
                        <HeaderCell><span className="header-cell">Section</span></HeaderCell>
                        <Cell dataKey="section" />
                    </Column>

                    <Column flexGrow={4}>
                        <HeaderCell><span className="header-cell">Room Name</span></HeaderCell>
                        <Cell dataKey="room_name" />
                    </Column>

                    <Column flexGrow={4}>
                        <HeaderCell><span className="header-cell">Bed Name</span></HeaderCell>
                        <Cell>{rowData => rowData.bed_name || "-"}</Cell>
                    </Column>

                    <Column flexGrow={4} align="right">
                        <HeaderCell><span className="header-cell" /></HeaderCell>
                        <Cell >
                            <EditIcon color="cyan" className="edit" />
                            <TrashIcon color="red" className="trash-bed" />
                        </Cell>
                    </Column>

                </TableWithSearch>
            </ConfigurationPage.Content>
        </>
    );
};

BedsList.defaultProps = {
    locationId: null,
    auth: null,
    history: null
}

export default BedsList;